import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/api";
import UpgradeModal from "./UpgradeModal";
import { getErrorMessage, requiresReauth, getErrorAction } from "../utils/errorMessages";
import '../styles/components/ImageUploadSection.css';

export default function ImageUploadSection({
  imageFile,
  setImageFile,
  labValues,
  setLabValues,
  ocrResult,
  setOcrResult,
  ocrCompleted: parentOcrCompleted,
  setOcrCompleted: setParentOcrCompleted,
  onOcrComplete,
}) {
  const { user, plan } = useAuth();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [ocrCompleted, setOcrCompleted] = useState(false);
  const [localLabValues, setLocalLabValues] = useState({});
  const [ocrResultData, setOcrResultData] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [error, setError] = useState('');
  const [errorAction, setErrorAction] = useState('');
  
  // Sync localLabValues to parent labValues automatically
  useEffect(() => {
    if (setLabValues && Object.keys(localLabValues).length > 0) {
      // Convert localLabValues (numeric only) to full formatted values for parent
      const formattedValues = {};
      const unitMapping = {
        'TSH': 'μIU/mL',
        'FT4': 'ng/dL',
        'FT3': 'pg/mL',
        'HbA1c': '%',
        '血糖値': 'mg/dL',
        'フェリチン': 'ng/mL'
      };
      
      Object.entries(localLabValues).forEach(([key, value]) => {
        if (value && value.toString().trim() !== '') {
          const unit = unitMapping[key] || '';
          formattedValues[key] = `${value} ${unit}`.trim();
        }
      });
      
      setLabValues(formattedValues);
    }
  }, [localLabValues, setLabValues]);
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setOcrCompleted(false); // 新しい画像が選択されたらOCR完了状態をリセット
    }
  };

  // 血液検査値の妥当性チェック（橋本病に必要な項目のみ）
  const validateLabValues = (labValues) => {
    const validRanges = {
      'TSH': { min: 0.1, max: 20, unit: 'μIU/mL', name: 'TSH' },
      'FT4': { min: 0.5, max: 5.0, unit: 'ng/dL', name: 'FT4' },
      'FT3': { min: 1.0, max: 10.0, unit: 'pg/mL', name: 'FT3' },
      'HbA1c': { min: 3.0, max: 15.0, unit: '%', name: 'HbA1c' },
      '血糖値': { min: 50, max: 500, unit: 'mg/dL', name: '血糖値' },
      'フェリチン': { min: 1, max: 1000, unit: 'ng/mL', name: 'フェリチン' }
    };

    const validatedValues = {};
    let hasValidData = false;

    Object.entries(labValues).forEach(([key, value]) => {
      // 必要な検査項目のみを処理
      const range = validRanges[key];
      if (!range) return; // 不要な項目は完全に無視

      const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
      
      if (!isNaN(numericValue) && numericValue >= range.min && numericValue <= range.max) {
        validatedValues[key] = value;
        hasValidData = true;
      }
      // 範囲外や不正な値は破棄（警告も表示しない）
    });

    return { validatedValues, hasValidData };
  };

  const handleUpload = async () => {
    if (!imageFile) return;
    
    setIsAnalyzing(true);
    setError('');
    setErrorAction('');
    const formData = new FormData();
    formData.append("image", imageFile);

    try {
      const res = await api.post("/api/upload", formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      const data = res.data;
      
      if (data.labValues) {
        // OCR結果の妥当性チェック
        const validation = validateLabValues(data.labValues);
        
        // 妥当性チェック結果を含めてOCR結果を保存
        setOcrResultData({
          ...data.labValues,
          _validation: validation
        });
        
        // ローカル状態にもOCR結果を事前設定（手動入力欄に表示するため）
        const localValues = {};
        Object.entries(data.labValues).forEach(([key, value]) => {
          // 数値部分のみを抽出して手動入力欄用の値として設定
          const numericValue = value.replace(/[^0-9.]/g, '');
          if (numericValue) {
            localValues[key] = numericValue;
          }
        });
        setLocalLabValues(localValues);
        
        // 妥当性チェックの結果に応じてメッセージを表示
        if (validation.hasValidData) {
          const validCount = Object.keys(validation.validatedValues).length;
          alert(`✅ 血液検査値の読み取りが完了しました！\n\n有効な検査値: ${validCount}件\n\n読み取った値を確認してください。\n画面を下にスクロールすると「次へ進む」ボタンがあります。`);
        } else {
          alert("⚠️ 有効な検査値を読み取れませんでした。\n\n手動入力欄で検査値を入力してください。\n画面を下にスクロールすると入力欄があります。");
        }
        
        // 妥当な値のみを親に渡す
        if (typeof setOcrResult === "function") {
          setOcrResult(data); // data全体（labValues含む）をセット
        }
        if (typeof setLabValues === "function") {
          setLabValues(validation.validatedValues); // 妥当な値のみをセット
        }
        setOcrCompleted(true);
        if (setParentOcrCompleted) setParentOcrCompleted(true);
      } else {
        setError("画像の文字認識に失敗しました。より鮮明な画像をお試しいただくか、手動で入力してください。");
        setErrorAction("別の画像を選択するか、手動で検査値を入力してください。");
      }
    } catch (err) {
      console.error("OCR upload failed:", err);
      
      // Use centralized error message handling
      const errorMessage = getErrorMessage(err);
      const actionMessage = getErrorAction(err);
      
      setError(errorMessage);
      setErrorAction(actionMessage);
      
      // Handle specific error cases
      if (err.response?.status === 403 && err.response?.data?.code === 'PREMIUM_REQUIRED') {
        setShowUpgradeModal(true);
      } else if (requiresReauth(err)) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  // プレミアムプラン以外の場合の制限表示
  const isPremium = plan === 'premium';
  const isLoggedIn = !!user;

  // プレミアムプラン以外には表示しない
  if (!isLoggedIn || !isPremium) {
    return (
      <div className="image-upload-section__premium">
        <div className="image-upload-section__premium-header">
          <h2 className="image-upload-section__premium-title">
            🩸 血液検査データ解析
          </h2>
          <span className="image-upload-section__premium-badge-large">
            プレミアム限定機能
          </span>
        </div>

        <div className="image-upload-section__premium-content">
          <div className="image-upload-section__premium-icon-wrapper">
            <svg className="image-upload-section__premium-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="image-upload-section__premium-subtitle">
              血液検査結果から詳細分析
            </h3>
          </div>

          <div className="image-upload-section__premium-features">
            <div className="image-upload-section__premium-feature">
              <p className="image-upload-section__premium-feature-text">✓ 画像の自動読み取り</p>
            </div>
            <div className="image-upload-section__premium-feature">
              <p className="image-upload-section__premium-feature-text">✓ 栄養素の過不足分析</p>
            </div>
            <div className="image-upload-section__premium-feature">
              <p className="image-upload-section__premium-feature-text">✓ 個別サプリ提案</p>
            </div>
            <div className="image-upload-section__premium-feature">
              <p className="image-upload-section__premium-feature-text">✓ 優先度付き改善計画</p>
            </div>
          </div>

          {!isLoggedIn ? (
            <div className="image-upload-section__premium-status">
              <p className="image-upload-section__premium-status-text">
                この機能を利用するにはログインが必要です
              </p>
            </div>
          ) : (
            <div className="image-upload-section__premium-status image-upload-section__premium-status--warning">
              <p className="image-upload-section__premium-status-text image-upload-section__premium-status-text--warning">
                現在のプラン: {plan === 'standard' ? 'スタンダード' : '無料'}プラン
              </p>
            </div>
          )}
        </div>

        <div className="image-upload-section__premium-cta">
          <button
            type="button"
            onClick={() => setShowUpgradeModal(true)}
            className="image-upload-section__upgrade-btn"
          >
            ⬆️ プレミアムにアップグレード
          </button>
        </div>

        {/* Upgrade Modal */}
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          trigger="ocr_feature"
        />
      </div>
    );
  }

  return (
    <div className="image-upload-section">
      {/* ヘッダー */}
      <div className="image-upload-section__header">
        <h2 className="image-upload-section__title">
          🩸 血液検査データ解析
        </h2>
        <span className="image-upload-section__badge">
          プレミアム機能
        </span>
      </div>

      {/* ステップ表示 */}
      <div className="image-upload-section__steps">
        <div className="image-upload-section__steps-track">
          {/* ステップ1: 画像選択 */}
          <div className={`image-upload-section__step ${imageFile ? '' : 'image-upload-section__step--inactive'}`}>
            <div className={`image-upload-section__step-number ${imageFile ? 'image-upload-section__step--active' : ''}`}>
              1
            </div>
            <p className="image-upload-section__step-label">画像選択</p>
          </div>

          {/* 区切り線1 */}
          <div className="image-upload-section__step-divider">
            <div className={`image-upload-section__step-progress ${ocrCompleted ? 'image-upload-section__step-progress--complete' : (imageFile ? 'image-upload-section__step-progress--partial' : '')}`}></div>
          </div>

          {/* ステップ2: 数値読み取り */}
          <div className={`image-upload-section__step ${ocrCompleted ? '' : 'image-upload-section__step--inactive'}`}>
            <div className={`image-upload-section__step-number ${ocrCompleted ? 'image-upload-section__step--active' : ''}`}>
              2
            </div>
            <p className="image-upload-section__step-label">数値読み取り</p>
          </div>

          {/* 区切り線2 */}
          <div className="image-upload-section__step-divider">
            <div className={`image-upload-section__step-progress ${ocrCompleted ? 'image-upload-section__step-progress--partial' : ''}`}></div>
          </div>

          {/* ステップ3: アドバイス生成 */}
          <div className="image-upload-section__step image-upload-section__step--inactive">
            <div className="image-upload-section__step-number">
              3
            </div>
            <p className="image-upload-section__step-label">アドバイス生成</p>
          </div>
        </div>
      </div>
      
      {/* 画像アップロードエリア */}
      {!imageFile ? (
        <div className="image-upload-section__dropzone">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="image-upload-section__file-input"
            id="image-upload"
          />
          <label htmlFor="image-upload" className="image-upload-section__file-label">
            <svg className="image-upload-section__upload-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="image-upload-section__upload-title">
              血液検査結果の画像をアップロード
            </p>
            <p className="image-upload-section__upload-hint">
              クリックして選択、またはドラッグ＆ドロップ
            </p>
            <p className="image-upload-section__upload-note">
              対応形式: JPG, PNG, GIF (最大10MB)
            </p>
          </label>
        </div>
      ) : (
        <div className="image-upload-section__file-card">
          <div className="image-upload-section__file-info">
            <svg className="image-upload-section__file-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div>
              <p className="image-upload-section__file-name">{imageFile.name}</p>
              <p className="image-upload-section__file-size">{(imageFile.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setImageFile(null);
              setOcrCompleted(false);
              if (setParentOcrCompleted) setParentOcrCompleted(false);
              setOcrResultData(null);
              setLocalLabValues({});
              setLabValues({});
            }}
            className="image-upload-section__file-remove"
          >
            <svg className="image-upload-section__file-remove-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      
      {/* OCR解析ボタン */}
      {!ocrCompleted && imageFile && (
        <button
          type="button"
          onClick={handleUpload}
          disabled={isAnalyzing}
          className="image-upload-section__analyze-btn"
        >
          {isAnalyzing ? (
            <span className="image-upload-section__btn-content">
              <svg className="image-upload-section__spinner" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              AI画像解析中...（約10-20秒）
            </span>
          ) : (
            <span className="image-upload-section__btn-content">
              <svg className="image-upload-section__btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              検査値を読み取る（ステップ2）
            </span>
          )}
        </button>
      )}

      {/* エラー表示 */}
      {error && (
        <div className="image-upload-section__error">
          <div className="image-upload-section__error-content">
            <span className="image-upload-section__error-icon">⚠️</span>
            <div>
              <h3 className="image-upload-section__error-title">
                画像処理エラー
              </h3>
              <p className="image-upload-section__error-message">{error}</p>
              {errorAction && (
                <p className="image-upload-section__error-action">{errorAction}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* OCR結果表示 */}
      {ocrCompleted && ocrResultData && Object.keys(ocrResultData).length > 0 && (
        <div className="image-upload-section__result">
          <div className="image-upload-section__result-header">
            <h3 className="image-upload-section__result-title">
              <svg className="image-upload-section__result-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              読み取り完了！
            </h3>
            <span className="image-upload-section__result-badge">ステップ2完了</span>
          </div>

          {/* 読み取り結果表示（有効な値のみ） */}
          {ocrResultData._validation && Object.keys(ocrResultData._validation.validatedValues).length > 0 ? (
            <div className="image-upload-section__result-grid">
              {Object.entries(ocrResultData._validation.validatedValues).map(([key, val]) => (
                <div key={key} className="image-upload-section__result-item">
                  <p className="image-upload-section__result-label">{key}</p>
                  <p className="image-upload-section__result-value">{val}</p>
                </div>
              ))}
            </div>
          ) : ocrResultData._validation && Object.keys(ocrResultData._validation.validatedValues).length === 0 ? (
            <div className="image-upload-section__result-empty">
              <p className="image-upload-section__result-empty-text">
                📋 有効な検査値を読み取れませんでした
              </p>
              <p className="image-upload-section__result-empty-hint">
                手動入力欄で検査値を入力してください
              </p>
            </div>
          ) : (
            // 従来の表示（妥当性チェックなし）
            <div className="image-upload-section__result-grid">
              {Object.entries(ocrResultData).map(([key, val]) => (
                <div key={key} className="image-upload-section__result-item">
                  <p className="image-upload-section__result-label">{key}</p>
                  <p className="image-upload-section__result-value">{val}</p>
                </div>
              ))}
            </div>
          )}

          <div className="image-upload-section__next-step">
            <p className="image-upload-section__next-step-content">
              <svg className="image-upload-section__next-step-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                <strong>✅ 読み取り完了</strong><br/>
                {ocrResultData._validation && Object.keys(ocrResultData._validation.validatedValues).length > 0 ?
                  '値を確認できたら、このすぐ下の「次へ進む」ボタンを押してください' :
                  '手動入力欄で値を修正できます。修正したら「次へ進む」を押してください'
                }
              </span>
            </p>

            {/* 手動入力セクション（OCR結果の直後に移動） */}
            <div className="image-upload-section__manual-inline">
              <h3 className="image-upload-section__manual-inline-title">
                ✏️ 値を修正・追加入力できます
              </h3>
              <p className="image-upload-section__manual-inline-description">
                読み取り結果が不正確な場合や、追加で入力したい項目がある場合にご利用ください
              </p>

              <div className="image-upload-section__manual-grid">
                {[
                  { key: 'TSH', label: 'TSH', unit: 'μIU/mL' },
                  { key: 'FT4', label: 'FT4', unit: 'ng/dL' },
                  { key: 'FT3', label: 'FT3', unit: 'pg/mL' },
                  { key: 'HbA1c', label: 'HbA1c', unit: '%' },
                  { key: '血糖値', label: '血糖値', unit: 'mg/dL' },
                  { key: 'フェリチン', label: 'フェリチン', unit: 'ng/mL' }
                ].map(({ key, label, unit }) => (
                  <div key={key} className="image-upload-section__manual-field">
                    <label className="image-upload-section__manual-label">{label} ({unit})</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="例: 2.5"
                      value={localLabValues[key] !== undefined ? localLabValues[key] : (labValues[key] ? labValues[key].replace(/[^0-9.]/g, '') : '')}
                      onChange={(e) => {
                        const value = e.target.value;
                        setLocalLabValues(prev => ({
                          ...prev,
                          [key]: value
                        }));
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (setLabValues) {
                          if (value) {
                            const newLabValues = {
                              ...labValues,
                              [key]: `${value} ${unit}`
                            };
                            setLabValues(newLabValues);
                          } else {
                            const newLabValues = { ...labValues };
                            delete newLabValues[key];
                            setLabValues(newLabValues);
                          }
                        }
                      }}
                      className="image-upload-section__manual-input"
                    />
                  </div>
                ))}
              </div>

              <p className="image-upload-section__manual-note">
                ※ 未入力の項目は血液検査データなしとしてアドバイスを生成します
              </p>
            </div>

            {onOcrComplete && (
              <button
                type="button"
                onClick={onOcrComplete}
                className="image-upload-section__next-step-button"
              >
                ✨ 次へ進む（アドバイスを受け取る）
              </button>
            )}
          </div>
        </div>
      )}

      {/* 手動入力セクション（OCR未実行時のみ表示） */}
      {!ocrCompleted && (
        <div className="image-upload-section__manual">
          <h3 className="image-upload-section__manual-title">
            <svg className="image-upload-section__manual-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            画像なしで手動入力する
          </h3>
          <p className="image-upload-section__manual-description">
            画像のアップロードをスキップして、検査値を直接入力することもできます
          </p>

          <div className="image-upload-section__manual-grid">
            {[
              { key: 'TSH', label: 'TSH', unit: 'μIU/mL' },
              { key: 'FT4', label: 'FT4', unit: 'ng/dL' },
              { key: 'FT3', label: 'FT3', unit: 'pg/mL' },
              { key: 'HbA1c', label: 'HbA1c', unit: '%' },
              { key: '血糖値', label: '血糖値', unit: 'mg/dL' },
              { key: 'フェリチン', label: 'フェリチン', unit: 'ng/mL' }
            ].map(({ key, label, unit }) => (
              <div key={key} className="image-upload-section__manual-field">
                <label className="image-upload-section__manual-label">{label} ({unit})</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="例: 2.5"
                  value={localLabValues[key] !== undefined ? localLabValues[key] : (labValues[key] ? labValues[key].replace(/[^0-9.]/g, '') : '')}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLocalLabValues(prev => ({
                      ...prev,
                      [key]: value
                    }));
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (setLabValues) {
                      if (value) {
                        const newLabValues = {
                          ...labValues,
                          [key]: `${value} ${unit}`
                        };
                        setLabValues(newLabValues);
                      } else {
                        const newLabValues = { ...labValues };
                        delete newLabValues[key];
                        setLabValues(newLabValues);
                      }
                    }
                  }}
                  className="image-upload-section__manual-input"
                />
              </div>
            ))}
          </div>

          <p className="image-upload-section__manual-note">
            ※ 未入力の項目は血液検査データなしとしてアドバイスを生成します
          </p>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        trigger="ocr_feature"
      />
    </div>
  );
}
